// FileUploader.tsx - Drag & drop file uploader component
import { useState, useRef } from 'react';
import {
  Paper,
  Group,
  Text,
  Button,
  Progress,
  Stack,
  Alert,
  Badge,
  ActionIcon,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconX,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { api } from '@/lib/api';

export interface FileUploaderProps {
  onUploadComplete?: (files: any[]) => void;
  entityType?: string;
  entityId?: string;
  accept?: string;
  maxSize?: number; // in MB
  multiple?: boolean;
}

export function FileUploader({
  onUploadComplete,
  entityType,
  entityId,
  accept = 'image/*',
  maxSize = 10,
  multiple = false,
}: FileUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (newFiles: File[]) => {
    setError(null);

    // Validate file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    const oversizedFiles = newFiles.filter(file => file.size > maxSizeBytes);
    
    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the maximum size of ${maxSize}MB`);
      return;
    }

    if (multiple) {
      setFiles(prev => [...prev, ...newFiles]);
    } else {
      setFiles(newFiles.slice(0, 1));
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    setProgress(0);
    const uploaded: any[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        
        if (entityType) formData.append('entityType', entityType);
        if (entityId) formData.append('entityId', entityId);

        const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/storage/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const result = await response.json();
        uploaded.push(result.data);
        setProgress(((i + 1) / files.length) * 100);
      }

      setUploadedFiles(uploaded);
      setFiles([]);
      
      if (onUploadComplete) {
        onUploadComplete(uploaded);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap="md">
      {/* Drop Zone */}
      <Paper
        p="xl"
        radius="md"
        withBorder
        style={{
          border: dragActive ? '2px dashed #2196f3' : '2px dashed #ddd',
          backgroundColor: dragActive ? '#f0f9ff' : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Stack align="center" gap="md">
          <IconUpload size={48} color="#2196f3" />
          <div style={{ textAlign: 'center' }}>
            <Text size="lg" fw={600}>
              Drop files here or click to browse
            </Text>
            <Text size="sm" c="dimmed" mt="xs">
              {accept === 'image/*' ? 'Images only' : 'All file types'} • Max {maxSize}MB
              {multiple && ' • Multiple files allowed'}
            </Text>
          </div>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleChange}
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Upload Error">
          {error}
        </Alert>
      )}

      {/* Selected Files */}
      {files.length > 0 && (
        <Paper p="md" withBorder>
          <Text size="sm" fw={600} mb="md">
            Selected Files ({files.length})
          </Text>
          <Stack gap="xs">
            {files.map((file, index) => (
              <Group key={index} justify="space-between">
                <Group gap="xs">
                  <IconFile size={20} />
                  <div>
                    <Text size="sm">{file.name}</Text>
                    <Text size="xs" c="dimmed">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </div>
                </Group>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>

          {/* Upload Progress */}
          {uploading && (
            <Progress value={progress} mt="md" animated />
          )}

          {/* Upload Button */}
          {!uploading && (
            <Button
              fullWidth
              mt="md"
              leftSection={<IconUpload size={16} />}
              onClick={uploadFiles}
            >
              Upload {files.length} {files.length === 1 ? 'File' : 'Files'}
            </Button>
          )}
        </Paper>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text size="sm" fw={600}>
              Uploaded Files
            </Text>
            <Badge color="green" leftSection={<IconCheck size={14} />}>
              {uploadedFiles.length} files
            </Badge>
          </Group>
          <Stack gap="xs">
            {uploadedFiles.map((file, index) => (
              <Group key={index} justify="space-between">
                <Group gap="xs">
                  <IconCheck size={20} color="green" />
                  <div>
                    <Text size="sm">{file.originalFilename}</Text>
                    <Text size="xs" c="dimmed">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </div>
                </Group>
                <Badge color="green" size="sm">
                  Uploaded
                </Badge>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
